/**
 * AppICEMAP — Local cache (local-first)
 *
 * The app used to show nothing until Directus answered, which on a cold start
 * (remote server, GPS, first poll) meant seconds of empty screen. Now the last
 * known reports and categories are kept in IndexedDB, painted immediately on
 * open, and replaced when the network catches up.
 *
 * Everything here is best-effort: a browser with IndexedDB blocked (private
 * mode, an old WebView) must not break the app, so every call resolves to a
 * harmless value instead of throwing.
 */

import { openDB } from "idb";

const DB_NAME = "appicemap";
const DB_VERSION = 1;
const STORE = "kv";

/** Cache keys */
export const CACHE_KEYS = {
	reports: "reports",
	categories: "categories",
};

/** @type {Promise<import('idb').IDBPDatabase>|null} */
let dbPromise = null;

function getDb() {
	if (!dbPromise) {
		dbPromise = openDB(DB_NAME, DB_VERSION, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(STORE)) {
					db.createObjectStore(STORE);
				}
			},
		}).catch((err) => {
			console.warn("[Cache] IndexedDB no disponible:", err.message);
			return null;
		});
	}
	return dbPromise;
}

/**
 * Read a cached value.
 * @param {string} key
 * @returns {Promise<{ value: any, savedAt: number } | null>}
 */
export async function readCache(key) {
	try {
		const db = await getDb();
		if (!db) return null;

		const entry = await db.get(STORE, key);
		if (!entry || !("value" in entry)) return null;

		return entry;
	} catch (err) {
		console.warn("[Cache] Lectura fallida:", err.message);
		return null;
	}
}

/**
 * Store a value. Fire-and-forget: callers should not await it on a hot path.
 * @param {string} key
 * @param {any} value
 * @returns {Promise<void>}
 */
export async function writeCache(key, value) {
	try {
		const db = await getDb();
		if (!db) return;

		// structuredClone-safe copy: proxies from the store cannot be stored
		await db.put(STORE, { value: JSON.parse(JSON.stringify(value)), savedAt: Date.now() }, key);
	} catch (err) {
		console.warn("[Cache] Escritura fallida:", err.message);
	}
}

/**
 * Drop everything (used on logout, where cached reports may be user-specific).
 * @returns {Promise<void>}
 */
export async function clearCache() {
	try {
		const db = await getDb();
		if (!db) return;
		await db.clear(STORE);
	} catch (err) {
		console.warn("[Cache] Limpieza fallida:", err.message);
	}
}
